<!--
  Copyright (C) 2020 ExploRêve

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see <https://www.gnu.org/licenses/>.

  Full source code is available at <https://gitlab.com/exploreve/frontend>.
-->

<template>
  <div class="apropos">
    <NavBar />
    <div class="container text-left mb-5">
      <h1>À propos de Explorêve</h1>
      <p>
        Explorêve, c'est un grand jeu virtuel pour les jeunes scouts, guides, éclaireuses et éclaireurs. Il est pensé
        pour des jeunes de 11 ans et plus, mais peut être joué par qui le souhaite. Il est prévu en plusieurs étapes,
        sur plusieurs semaines.
      </p>
      <h5 style="text-align: center;">
        <a href="#naissance">La naissance, l'esprit et les objectifs du projet</a> -
        <a href="#contact">Nous contacter</a> - <a href="#trombi">L'équipe</a><br />
        <a href="#legal">Crédits et mentions légales</a> -
        <a href="#perso">Informations sur tes données personnelles</a>
      </h5>
      <br />
      <img
        class="w-50 mt-3 mr-4 mb-sm-2 float-none float-sm-left"
        src="https://cdn.exploreve.fr/content/carte/maison.svg"
        alt="Maison"
        style="margin-top: 50px !important;"
      />
      <h2><a name="naissance">La naissance du projet</a></h2>
      <p>
        Le projet est né dans le contexte de la pandémie de COVID-19, alors que pour protéger leurs membres, les
        mouvements de scoutisme en France annulaient toutes leurs activités en présentiel.
      </p>
      <p>
        Comment continuer à faire vivre le scoutisme pendant cette période ? Comment ne pas perdre le lien avec les
        jeunes que nous encadrons toute l'année ? Comment les accompagner et les aider à s'évader, aussi, alors que
        l'actualité peut être source d'inquiétudes ? Toutes ces questions nous sont venues rapidement en tête, à nous
        chefs, cheftaines, responsables d'animation bénévoles de différents mouvements.
      </p>
      <p>
        Mais pour que tout démarre vraiment, il a fallu un tweet. Quelques heures après, des bénévoles de plusieurs
        mouvements de scoutisme commençaient à s'échanger des idées, à dresser des premières hypothèses. Un salon
        Discord a été monté, des invitations lancées. Le lendemain, nous étions 15 et tenions la première réunion pour
        poser les objectifs pédagogiques du futur jeu. Le surlendemain l'imaginaire était trouvé et les premiers choix
        techniques posés. Le sursurlendemain, une nouvelle vague de bénévoles est arrivée. Nous étions 30, sans compter
        une équipe qui compilait en parallèle toutes les idées d'activités à distance qui fleurissaient un peu partout.
        Nous avons pris conscience, un peu hébété·es, de la multitude de compétences rassemblées en si peu de temps :
        graphistes, développeurs et développeuses, musiciens et musiciennes, rédacteurs et rédactrices, spécialistes des
        jeu de pistes et des codes, ... Avec un minimum de coordination, tout ce beau monde s'est lancé dans un travail
        acharné, avec l'objectif de sortir la première étape du jeu ... dix jours après la première réunion. Nous y
        voici.
      </p>

      <h2>L'esprit général</h2>
      <p>
        Le projet est porté et réalisé par des bénévoles, majoritairement des jeunes adultes, membres de plusieurs
        associations de scoutisme en France. Cette dimension inter-mouvement est importante pour nous, elle fait la
        richesse de ce projet, qui est aussi -mine de rien- une très belle occasion de nous rencontrer.
      </p>
      <p>
        Nous fonctionnons de manière collaborative, par équipes, en essayant autant que possible de ne pas instaurer de
        hiérarchie dans le groupe. En revanche, il existe des fonctions de coordination. Nous tenons à accueillir les
        nouveaux et nouvelles venu·es au fur et à mesure.
      </p>

      <h2>Les objectifs pédagogiques du jeu</h2>
      <h5>Montrer la diversité des manières et des contextes dans lesquels on peut vivre du scoutisme</h5>
      <ul>
        <li>Chaque participant·e aura échangé avec au moins une personne d'un autre mouvement</li>
        <li>Les participant·es auront eu l'occasion de découvrir des pans de l'histoire du scoutisme</li>
        <li>
          Les participant·es auront eu la possibilité de réflechir à de nouvelles manières de vivre le scoutisme
        </li>
      </ul>
      <h5>Expérimenter le numérique au service du collectif et de la production de connaissances communes</h5>
      <ul>
        <li>Les participant·es se seront servi d'outils collaboratifs en ligne</li>
        <li>
          Les participant·es auront l'occasion de réfléchir aux outils numériques issus de pratiques collaboratives
          qu'ils et elles utilisent déjà
        </li>
      </ul>
      <h5>Vivre une expérience pour "sortir" du confinement</h5>
      <ul>
        <li>
          Les participant·es auront l'occasion de créer des liens entre leur environnement direct et leur expérience
          numérique
        </li>
        <li>Les participant·es auront des supports pour exprimer leur créativité</li>
        <li>Les participant·es pourront participer à un récit interactif collectif</li>
      </ul>

      <h2><a name="contact">Pour nous contacter</a></h2>
      <p>
        Un petit message? Une question ? Un problème ? Une suggestion ? écris-nous à
        <a href="mailto:contact@exploreve.fr">contact@exploreve.fr</a>
      </p>
      <p>Rejoins-nous sur nos réseaux sociaux :</p>
      <ReseauxSociaux class="mt-2" />

      <h2><a name="trombi">Le trombinoscope de l’équipe></a></h2>

      <div class="row justify-content-md-center" id="portfolio">
        <div v-for="member in shuffleMembers(members)" :key="member.id" class="col-6 col-md-3">
          <div class="portfolio-thumb">
            <img :src="member.image_url" class="img-responsive" alt="Portfolio" />
            <div class="portfolio-overlay">
              <div class="portfolio-item">
                <h4>{{ member.display_name }}</h4>
                <p>{{ member.movement.replace("/", "") }}</p>
                <small v-for="role in member.roles" :key="role"> {{ role }}<br /></small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h2><a name="legal">Crédits et mentions légales</a></h2>
      <p>
        Explorêve est un jeu façonné avec soin par une équipe de scouts, guides, éclaireurs et éclaireuses de plusieurs
        mouvements de scoutisme. Tous les éléments du jeu, des illustrations aux musiques en passant par les énigmes,
        ont été réalisés par cette équipe d'amateurs et d'amatrices du froissartage numérique.
      </p>

      <p>
        Adeptes du partage, des outils et des savoirs communs, nous avons fait le choix de mettre les créations
        produites pour le jeu sous deux licences creative commons ou AGPL:
      </p>
      <ul>
        <li>
          L'ensemble des illustrations sont sous licence
          <a href="https://creativecommons.org/licenses/by-sa/3.0/fr/">CC BY-SA</a>. Vous êtes libre de les partager,
          les réutiliser ou les modifier, de les utiliser pour un usage commercial ou non tant que vous créditez
          Explorêve et que votre création est également sous cette même licence.
        </li>
        <li>
          L'ensemble de la musique réalisée pour le jeu est sous licence
          <a href="https://creativecommons.org/licenses/by-nc-nd/3.0/fr/">CC BY-NC-ND</a>. Vous êtes libre de les
          partager, les télécharger et les réutiliser tant que vous créditez Explorêve. En revanche, vous ne pouvez pas
          les modifier ou en faire un usage commercial.
        </li>
        <li>
          L'ensemble des textes, voix enregistrées et concepts de jeux sont sous licence
          <a href="https://creativecommons.org/licenses/by-nc-sa/3.0/fr/">CC BY-NC-SA</a> Vous êtes libre de les
          partager, les réutiliser ou les modifier, tant que vous créditez Explorêve et que votre création est également
          sous cette même licence. En revanche, vous ne pouvez pas en faire un usage commercial.
        </li>

        <li>
          L'ensemble du code source du site est sous licence
          <a href="https://www.gnu.org/licenses/agpl-3.0.fr.html">AGPLv3</a>. Il est disponible
          <a href="https://gitlab.com/exploreve">sur GitLab</a>. Vous êtes libres de le télécharger, modifier, compiler,
          mais dès lors que d'autres personnes ont accès au service, vous devez publier vos modifications sous AGPL
          aussi.
        </li>
      </ul>
      <p>
        Nous avons également fait le choix d'utiliser uniquement (à l'exception de Discord et de Jigsaw) des outils sous
        licence libre. C'est aussi le cas des quelques éléments utilisés dans le jeu et qui n'ont pas été créés avec nos
        petites mains :
      </p>
      <ul>
        <li>
          Le magnifique caractère Faune, a été créé par Alice Savoie dans le cadre d’une commande du Centre national des
          arts plastiques en partenariat avec le Groupe Imprimerie Nationale. Licence CC BY-ND 4.0
        </li>
        <li>
          La merveilleuse carte de la Troposphère est basée sur
          <a href="https://www.openstreetmap.org">OpenStreetMap</a>, le projet de cartographie collaborative sous
          licence libre. Nous utilisons un fond de carte réalisé par
          <a href="https://www.thunderforest.com/">Thunderforest</a> et avons utilisé la librairie open-source
          <a href="https://leafletjs.com/">Leaflet</a>
        </li>
        <li>Dans la première immersion, plusieurs images, issues de WikimediaCommons :</li>
        <ul>
          <li>
            Le puzzle ballon-songe est une modification de
            <a href="https://commons.wikimedia.org/wiki/File:Early_flight_02562u_(7).jpg">cette image</a> (Domaine
            public)
          </li>
          <li>
            L'aide du morse est extraite de
            <a href="https://commons.wikimedia.org/wiki/File:International_Morse_Code-fr.svg">cette image</a>
            (Domaine public)
          </li>
          <li>
            L'aide de l'azimut est une modification de
            <a href="https://commons.wikimedia.org/wiki/File:Azimut.svg">cette image</a> (Licence CC BY-SA 3.0)
          </li>
        </ul>
        <li>Dans la seconde immersion, plusieurs images, issues de WikimediaCommons :</li>
        <ul>
          <li>
            Les visuels en sémaphore sont issus de <a href="https://fr.scoutwiki.org/">Scoutopedia</a> et ont été
            réalisé par Sezione di Rovereto de l'association italienne CNGEI.
          </li>
          <li>
            Les visuels en dactylologie (alphabet en langue des signes française) sont issus de
            <a href="https://fr.wikiversity.org/">Wikiversité</a> et sont dans le domaine public.
          </li>
          <li>
            L'enregistrement de la chanson du Jamboree de 1947 est issu d'un séjour de l'Association des Anciens
            Eclaireurs et Eclaireuses de France, disponible
            <a href="https://www.youtube.com/watch?v=6QzV59Y7F64">ici</a>. La chanson du Jamboree a été composée par
            Louis Liébard et Georges Tisserand.
          </li>
        </ul>
      </ul>

      <p>
        Pour travailler toutes et tous ensemble, nous avons utilisés plusieurs outils : Discord, Etherpad, GitLab et
        Seafile hébergé sur <a href="https://tila.im/">Tila.im</a>.
      </p>

      <p>
        Nous tenons à remercier chaleureusement <a href="https://www.clever-cloud.com/">Clever Cloud</a> et Julien qui
        nous prêtent leurs serveurs pour héberger l'Explorêve (nous ne sommes pas encore parvenu à l'héberger dans la
        troposphère).
      </p>

      <h2><a name="perso">Informations sur tes données personnelles dans Explorêve</a></h2>
      <p>En introduction, on souhaite préciser que le projet Explorêve a essayé, dès le début, de :<br /></p>
      <ul>
        <li>
          ne pas nécessiter de création de compte sur notre site ou sur un autre, notamment pour éviter de contraindre
          les joueurs et joueuses à donner des informations personnelles pour pouvoir jouer
        </li>
        <li>
          limiter le recueil et l’affichage d’informations identifiantes (ça veut dire : des informations qui permettent
          de savoir qui tu es) à la fois par principe, et dans un souci de protection des mineur·es qui jouent
        </li>
      </ul>
      <h5>Quelles données personnelles recueillons-nous ?</h5>
      <p>
        A. Dans le jeu, on recueille ton prénom/pseudo (mais pas ton nom de famille), ton mouvement de scoutisme et ton
        âge à partir de la deuxième aventure. Ces informations sont transmises à d’autres joueurs ou joueuses.<br />
        B. Si tu es abonné·e à la newsletter, on recueille ton adresse mail<br />
        C. On recueille aussi des informations pour savoir combien de gens jouent et qu’ils ou elles puissent revenir à
        l’endroit quitté : ton adresse IP et des informations de navigation tel que ton navigateur ou ton système
        d’exploitation : ce sont les seules informations que l’on recueille sans que tu nous les donnes toi-même.
      </p>
      <p>
        Ces données servent uniquement pour Explorêve : on ne les vend pas. On va sans doute les transférer dans
        quelques temps à l’association <a href="https://www.latoilescoute.net">LaToileScoute</a>, qui va probablement
        héberger le site pour qu’il soit encore accessible après la fin du confinement. A ce moment-là, on supprimera
        les mails et les informations de suivi.
      </p>
      <h5>Qui a accès à ces données et pendant combien de temps ?</h5>
      <p>
        Les personnes qui ont accès à ces données sont :
      </p>
      <ul>
        <li>les bénévoles du projet Explorêve</li>
        <li>d’autres joueurs et joueuses pour les données citées en A. ci-dessus</li>
      </ul>
      <p>Nous avons décidé de conserver ces données :</p>
      <ul>
        <li>sans limite de durée pour les données du A.</li>
        <li>
          jusqu’à la fin de la phase de création du jeu et le transfert à LaToileScoute (vers juin 2020) pour les B. et
          C.
        </li>
      </ul>
      <h5>Quels sont tes droits ?</h5>
      <p>Tu peux nous demander de :</p>
      <ul>
        <li>de consulter et rectifier tes données que l’on conserve</li>
        <li>
          les supprimer:
          <ul>
            <li>si tu veux supprimer ton adresse mail, tu ne recevras plus les mails de la newsletter</li>
            <li>
              si tu veux supprimer les autres informations, cela ne t’empêchera pas de continuer à jouer ou de rejouer
              si tu le souhaites
            </li>
          </ul>
        </li>
      </ul>
      <p>
        Pour nous contacter et exercer ces droits, écris à contact@exploreve.fr. En principe on répond dans la semaine
        (sauf si tout le monde est en camp…).
      </p>

      <p>
        Si tu estimes que l’on ne respecte pas tes droits concernant tes données personnelles, tu peux contacter la CNIL
        (mais dans l’idéal, tu peux nous en parler avant :-) ). N’hésite pas, plus généralement, à consulter
        <a href="https://www.cnil.fr/fr/les-droits-pour-maitriser-vos-donnees-personnelles">le site de la CNIL</a> pour
        connaître tes droits dans ce domaine.
      </p>
    </div>
  </div>
</template>

<script>
// @ is an alias to /src
import NavBar from "@/components/NavBar.vue";
import ReseauxSociaux from "@/components/ReseauxSociaux.vue";
import ApiService from "@/services/ApiService";
import _ from "lodash";

export default {
  name: "Apropos",
  components: {
    NavBar,
    ReseauxSociaux
  },
  data: () => {
    return {
      members: [],
      role: ""
    };
  },
  mounted() {
    ApiService.get("/trombi").then((response) => {
      this.members = response.data.trombi;
    });
  },
  methods: {
    shuffleMembers(members) {
      return _.shuffle(members);
    }
  }
};
</script>

<style lang="scss" scoped>
.apropos {
  background-color: white;
  padding-bottom: 1px; /* Fix problème d'affichage du bouton contact */
}
h5 {
  font-weight: 600;
}
h2 {
  margin-top: 3rem;
}
p {
  font-size: 1.1em;
}

.reseaux-sociaux {
  width: 250px;
}

#portfolio {
  small {
    opacity: 0.9;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  p {
    text-transform: uppercase;
  }

  .portfolio-thumb {
    position: relative;
    padding: 0;
    margin-top: 32px;
    box-shadow: 0px 5px 25px 0px rgba(0, 0, 0, 0.2);

    > img {
      width: 100%;
    }

    .portfolio-overlay {
      position: absolute;
      background: $dark-grey;
      color: white;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      width: 100%;
      height: 100%;
      text-align: center;
      vertical-align: top;
      opacity: 0;
      transition: all 0.3s ease-in-out;
      font-size: 0.8em;

      @media screen and(min-width: $breakpoint-lg) {
        font-size: 1em;
      }

      .portfolio-item {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 100%;
      }
    }

    &:hover .portfolio-overlay {
      opacity: 0.9;
    }
  }
}
</style>
